import React from 'react';
import { Email, Box, Item, Span, A, Image, renderEmail } from '../../library/react-html-email';
import EmailVehicleSpec from './EmailVehicleSpec';
import EmailApplicationSection from './EmailApplicationSection';

// Global email CSS - inline styles for maximum compatibility
const css = `
  * { border: none; outline: 0 }
  body,html { background-color: #ededed; margin: 0; }
  body,html,table,table td { font-size: 16px; font-family: Arial, sans-serif !important }
  body { -webkit-text-size-adjust: none; -ms-text-size-adjust: none; line-height: 1.5 }
  table { mso-table-lspace: 0; mso-table-rspace: 0; table-layout: fixed }
  table table { table-layout: auto }
  table,table td { border-collapse: collapse }
  img { border: 0; display: block; max-width: 100% }
  a { color: #D5000A; text-decoration: underline }
`.trim();

/**
 * EmailSpecTemplate - Main email template for product recommendations
 * Renders complete vehicle spec page as email-safe HTML
 * Uses react-html-email for table-based layout compatible with all email clients
 */
const EmailSpecTemplate = function (props) {
  const { equipment, copy, t, registration, getProductData, selectedFamily, query = {} } = props;
  const assetPath = copy.asset_path;

  // Group applications by type
  const engineApplications = equipment.applications?.filter(app => app.type === 'engine') || [];
  const otherApplications = equipment.applications?.filter(app => app.type !== 'engine') || [];


  // Get disclaimer text - using correct translation keys
  const disclaimerTitle = t('disclaimer.show_link') || 'Disclaimer';
  const disclaimerText = t('disclaimer.copy') || '';
  const disclaimerFull = t('disclaimer.full_copy') || '';

  return (
    <Email title={copy.title} headCSS={css} width="100%" bgcolor="#ededed">
      <Item align="center">
        {/* Main Container - 600px wide for email compatibility */}
        <Box width="600" bgcolor="#ededed" align="center" style={{ marginTop: '24px' }}>

          {/* Header with Logo */}
          <Item id="Header" align="center" bgcolor="#001450">
            <Box width="600" bgcolor="#001450" align="left">
              <Item align="left" style={{ height: '80px', padding: '0 24px' }}>
                <A href="http://www.mobil.com/">
                  <Image
                    src={assetPath + 'images/email/logo.png'}
                    width={112}
                    height={32}
                    alt="Mobil"
                  />
                </A>
              </Item>
            </Box>
          </Item>

          {/* Title */}
          <Item id="Title" align="center" >
            <Box width="600" bgcolor="#ededed" align="left" style={{ marginTop: '24px', marginBottom: '24px' }}>
              <Item align="left">
                <Span fontSize={28} color="#202020" style={{ lineHeight: '1.3', fontWeight: '400' }}>
                  {t('vehicle_spec.recommendation_title')}
                </Span>
              </Item>
            </Box>
          </Item>

          {/* Vehicle Specification */}
          <Item id="VehicleSpec" align="center">
            <Box width="600" bgcolor="#ffffff" align="center" style={{ borderRadius: '8px', marginBottom: '24px', overflow: 'hidden', backgroundColor: '#ffffff' }}>
              <EmailVehicleSpec
                equipment={equipment}
                registration={registration}
                t={t}
                assetPath={assetPath}
                selectedFamily={selectedFamily}
              />
            </Box>
          </Item>

          {/* Engine Applications */}
          {engineApplications.length > 0 && (
            <Item id="EngineApplications" align="center">
              <Box width="600" align="center" style={{ marginBottom: '24px' }}>
                {engineApplications.map((app) => (
                  <EmailApplicationSection
                    key={app.id}
                    application={app}
                    equipment={equipment}
                    t={t}
                    assetPath={assetPath}
                    getProductData={getProductData}
                    query={query}
                  />
                ))}
              </Box>
            </Item>
          )}

          {/* Other Applications */}
          {otherApplications.length > 0 && (
            <Item id="OtherApplications" align="center">
              <Box width="600" align="center" style={{ marginBottom: '24px' }}>
                {otherApplications.map((app) => (
                  <EmailApplicationSection
                    key={app.id}
                    application={app}
                    equipment={equipment}
                    t={t}
                    assetPath={assetPath}
                    getProductData={getProductData}
                    query={query}
                  />
                ))}
              </Box>
            </Item>
          )}


          {/* Disclaimer */}
          <Item id="Disclaimer" align="center" bgcolor="#ededed">
            <Box width="600" bgcolor="#ededed" align="center" style={{ padding: '40px 20px' }}>
              <Item style={{ paddingBottom: '20px' }}>
                <Span fontSize={18} color="#202020" style={{ fontWeight: '600', display: 'block' }}>
                  {disclaimerTitle}
                </Span>
              </Item>
              <Item style={{ paddingBottom: '16px' }}>
                <Span fontSize={13} color="#606060" style={{ lineHeight: '1.6' }}>
                  {disclaimerText}
                </Span>
              </Item>
              {disclaimerFull && (
                <Item>
                  <Span fontSize={12} color="#808080" style={{ lineHeight: '1.6' }}>
                    {disclaimerFull}
                  </Span>
                </Item>
              )}
            </Box>
          </Item>

          {/* Bottom Spacer for Outlook compatibility */}
          <Item id="BottomSpacer" align="center" bgcolor="#ededed" style={{ height: '48px', lineHeight: '48px', fontSize: '1px' }}>
            &nbsp;
          </Item>

        </Box>
      </Item>
    </Email>
  );
};

/**
 * GetEmailSpecTemplate - Renders the template to HTML string
 * @param {Object} props - Template props including equipment, copy, t, registration, getProductData
 * @returns {String} HTML string ready to send as email body
 */
export const GetEmailSpecTemplate = function (props) {
  try {
    // Validate required props
    if (!props || !props.equipment || !props.copy || !props.t) {
      throw new Error('Missing required props for email template');
    }

    const html = renderEmail(<EmailSpecTemplate {...props} />);

    // Validate that renderEmail returned a string
    if (typeof html !== 'string') {
      throw new Error('renderEmail did not return a valid HTML string');
    }

    return html;
  } catch (error) {
    console.error('GetEmailSpecTemplate: Error rendering email template:', error);
    // Return a basic fallback HTML if rendering fails
    return `
      <html>
        <body style="font-family: Arial, sans-serif; padding: 20px;">
          <h2>${props.copy.title || 'Your Mobil™ product recommendation'}</h2>
          <p>${props.copy.message || 'Please find your requested Mobil product recommendation.'}</p>
          <p style="color: #666; font-size: 12px;">Error: Unable to generate full email template.</p>
        </body>
      </html>
    `;
  }
};

export default EmailSpecTemplate;
